import { useCallback, useMemo } from 'react';
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import type { ClientOrdersView } from '@/features/customers/CustomerOrdersSection';
import { CustomerRecordPage } from '@/features/customers/CustomerRecordPage';
import { CUSTOMER_TABS, type CustomerTab } from '@/features/customers/copy';
import { CLIENT_ORDER_STATUSES } from '@/features/orders/orders';

/*
  Which tab shows travels in the URL as `?tab=styling`, so a reload keeps it and a link opens on
  it (asked for on 17 Sep 2026).

  Optional rather than required, and absent for the profile. That keeps the ordinary link to a
  client the bare `/customers/65` the register already writes, so nothing else has to learn about
  this parameter, and it leaves `?tab=` meaning "somebody chose this". Anything unrecognised lands
  on the profile the way the product registers' `?register=` lands on the first tab, rather than
  throwing the route over a mistyped URL.
*/
const recordSearch = z.object({
    tab: z.enum(CUSTOMER_TABS).optional().catch(undefined),
    /*
      The Wallet & orders tab's Orders card: its status and its page (ALS-169). In the URL for the
      same reason as the tab, so Back from an order lands on the view it was opened from. Both are
      absent at their defaults, and switching tab drops them.
    */
    orderStatus: z.enum(CLIENT_ORDER_STATUSES).optional().catch(undefined),
    orderPage: z.coerce.number().int().min(1).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/customers/$customerId')({
    validateSearch: (search) => recordSearch.parse(search),
    component: CustomerRoute,
});

const DEFAULT_TAB = CUSTOMER_TABS[0];

function CustomerRoute() {
    const { customerId } = Route.useParams();
    const { tab, orderStatus, orderPage } = Route.useSearch();
    const navigate = useNavigate();
    const id = Number(customerId);

    // Memoised on the two values, because the orders table closes its open row when this changes.
    const orders = useMemo<ClientOrdersView>(
        () => ({ status: orderStatus ?? null, page: orderPage ?? 1 }),
        [orderStatus, orderPage],
    );
    const onOrdersChange = useCallback(
        (next: ClientOrdersView) =>
            void navigate({
                from: '/customers/$customerId',
                to: '/customers/$customerId',
                params: { customerId },
                search: (prev) => ({
                    ...prev,
                    orderStatus: next.status ?? undefined,
                    orderPage: next.page > 1 ? next.page : undefined,
                }),
                replace: true,
                resetScroll: false,
            }),
        [navigate, customerId],
    );

    // A junk id in the URL is a dead link, not a record: the not-found page says so rather than
    // the record page asking the API about NaN.
    if (!Number.isInteger(id) || id <= 0) {
        throw notFound();
    }
    return (
        <CustomerRecordPage
            customerId={id}
            tab={tab ?? DEFAULT_TAB}
            onTabChange={(next: CustomerTab) =>
                void navigate({
                    to: '/customers/$customerId',
                    params: { customerId },
                    // The profile drops the parameter rather than spelling out the default.
                    search: next === DEFAULT_TAB ? {} : { tab: next },
                    /*
                      Replace, so four tabs do not become four back-button steps on one record.
                      The leave prompt still guards an unsaved draft: useBlocker fires on a
                      replace as it does on a push.
                    */
                    replace: true,
                    resetScroll: false,
                })
            }
            orders={orders}
            onOrdersChange={onOrdersChange}
        />
    );
}
